import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  Firestore,
} from 'firebase/firestore';
import { FirebaseAppService } from './firebase-app';
import { Organization, OrganizationMember, OrganizationRef, OrganizationVisibility } from '../interfaces/organization';
import { Log } from './log';

/**
 * Free/personal email domains that should not be used for organization domain matching.
 */
const IGNORED_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'ymail.com',
  'hotmail.com', 'hotmail.co.uk',
  'outlook.com', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com',
  'protonmail.com', 'proton.me',
  'yandex.com', 'yandex.ru',
  'mail.com',
  'zoho.com',
]);

class OrganizationsController {

  private get db(): Firestore {
    return getFirestore(FirebaseAppService.app);
  }

  toRef(org: Organization): OrganizationRef {
    return { id: org.id, name: org.name, ownerId: org.ownerId, licenseTier: org.licenseTier, visibility: org.visibility };
  }

  async getOrganization(orgId: string): Promise<Organization | null> {
    try {
      const snap = await getDoc(doc(this.db, 'organizations', orgId));
      return snap.exists() ? (snap.data() as Organization) : null;
    } catch (error) {
      Log.error('Failed to get organization', error);
      throw error;
    }
  }

  /**
   * Find an organization whose domain matches the given email domain.
   * Returns null if the domain is in the ignored list or no match is found.
   */
  async findByDomain(emailDomain: string): Promise<Organization | null> {
    if (IGNORED_EMAIL_DOMAINS.has(emailDomain.toLowerCase())) {
      return null;
    }
    try {
      const q = query(
        collection(this.db, 'organizations'),
        where('domain', '==', emailDomain.toLowerCase()),
      );
      const snap = await getDocs(q);
      if (snap.empty) return null;
      return snap.docs[0].data() as Organization;
    } catch (error) {
      Log.error('Failed to find organization by domain', error);
      throw error;
    }
  }

  async createOrganization(
    name: string,
    ownerId: string,
    visibility: OrganizationVisibility,
    domain: string,
    autoGrantAccessForDomain: boolean,
    createdBy: string,
  ): Promise<Organization> {
    try {
      const ref = await addDoc(collection(this.db, 'organizations'), {
        name,
        ownerId,
        visibility,
        domain: domain.toLowerCase(),
        autoGrantAccessForDomain,
        groups: [],
        workflows: [],
        createdAt: serverTimestamp(),
        createdBy,
        updatedAt: serverTimestamp(),
        updatedBy: createdBy,
      });

      await setDoc(doc(this.db, 'organizations', ref.id), { id: ref.id }, { merge: true });

      const org: Organization = {
        id: ref.id,
        name,
        ownerId,
        licenseTier: 'free',
        visibility,
        domain: domain.toLowerCase(),
        autoGrantAccessForDomain,
        groups: [],
        createdAt: new Date(),
        createdBy,
        updatedAt: new Date(),
        updatedBy: createdBy,
      };
      Log.debug('Organization created', ref.id);
      return org;
    } catch (error) {
      Log.error('Failed to create organization', error);
      throw error;
    }
  }

  async updateOrganization(orgId: string, data: Partial<Pick<Organization, 'name' | 'autoGrantAccessForDomain'>>, updatedBy: string): Promise<void> {
    try {
      await setDoc(doc(this.db, 'organizations', orgId), {
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy,
      }, { merge: true });
      Log.debug('Organization updated', orgId);
    } catch (error) {
      Log.error('Failed to update organization', error);
      throw error;
    }
  }

  private membersCol(orgId: string) {
    return collection(this.db, 'organizations', orgId, 'members');
  }

  async getMembers(orgId: string): Promise<OrganizationMember[]> {
    try {
      const snap = await getDocs(this.membersCol(orgId));
      return snap.docs.map(d => d.data() as OrganizationMember);
    } catch (error) {
      Log.error('Failed to get organization members', error);
      throw error;
    }
  }

  async getMember(orgId: string, userId: string): Promise<OrganizationMember | null> {
    try {
      const snap = await getDoc(doc(this.membersCol(orgId), userId));
      return snap.exists() ? (snap.data() as OrganizationMember) : null;
    } catch (error) {
      Log.error('Failed to get organization member', error);
      throw error;
    }
  }

  async addMember(orgId: string, member: OrganizationMember): Promise<void> {
    try {
      await setDoc(doc(this.membersCol(orgId), member.user.id), member);
      Log.debug(`Member ${member.user.id} added to organization ${orgId}`);
    } catch (error) {
      Log.error('Failed to add member to organization', error);
      throw error;
    }
  }

  async removeMember(orgId: string, userId: string): Promise<void> {
    try {
      await deleteDoc(doc(this.membersCol(orgId), userId));
      Log.debug(`Member ${userId} removed from organization ${orgId}`);
    } catch (error) {
      Log.error('Failed to remove member from organization', error);
      throw error;
    }
  }

  async deleteOrganization(orgId: string): Promise<void> {
    try {
      await deleteDoc(doc(this.db, 'organizations', orgId));
      Log.debug('Organization deleted', orgId);
    } catch (error) {
      Log.error('Failed to delete organization', error);
      throw error;
    }
  }

  isIgnoredDomain(domain: string): boolean {
    return IGNORED_EMAIL_DOMAINS.has(domain.toLowerCase());
  }
}

export const OrganizationsService = new OrganizationsController();
